import { SubmissionFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/submission-fields/submission-form-fields';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import _ from 'lodash';

import {
  FieldType,
  IFieldStateExternalDependency
} from '@publishing-engine/domain/types';
import { ISubmissionFileSet } from '@publishing-engine/domain/models';
import { useWorkflow } from './useWorkflow';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@morressier/alembic';
import {
  useAcceptTransfer,
  useEditSubmissionMetadata,
  useSubmitLicensing,
  useAuthorCompleteChangeRequest,
  useSubmitRevision,
  useAuthorEditSubmission,
  useEditSubmissionArticleType
} from 'src/api/commands';
import {
  getSubmissionRequestContextFromRoute,
  useSubmissionData
} from '../contexts/SubmissionDataProvider';
import { ISubmissionFieldResponseObject } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { IGetSubmissionFieldsResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import {
  getReviewRequestContextFromRoute,
  useReviewData
} from 'src/contexts/ReviewDataProvider';
import { getFiguresMetadata, getFileMetadata } from 'src/utils/file';
import { useCallback } from 'react';
import {
  getAssessmentRequestContextFromRoute,
  useAssessmentData
} from 'src/contexts/AssessmentDataProvider';
import {
  getAdjudicationRequestContextFromRoute,
  useAdjudicationData
} from 'src/contexts/AdjudicationDataProvider';
import { useCommandResponse } from 'src/contexts/SourceFileCommandResponseContex';
import { getSourceFileSetSignature } from 'src/pages/Organization/public/Journal/SubmissionUpload/fileSetSignature';
const omitId = (values: (unknown & { _id: string })[]) =>
  values?.map(value => _.omit(value, ['_id', 'id']));

const parseDependentField = (
  value:
    | {
        exists: boolean;
        value: unknown | (unknown & { _id: string })[];
        description?: unknown | (unknown & { _id: string });
      }
    | null
    | undefined
) =>
  value
    ? {
        exists: value.exists,
        value: Array.isArray(value.value) ? omitId(value.value) : value.value,
        description: value?.description ?? ''
      }
    : null;

type IEditableFieldState<FFD extends FormFieldDefinition> = {
  name: FFD['name'];
  formFieldDefinition: FFD;
  value: FieldType<FFD>;
  previousValue: FieldType<FFD>;
  isEditable: true;
};

type INonEditableFieldState<FFD extends FormFieldDefinition> = {
  name: FFD['name'];
  isEditable: false;
  value: FieldType<FFD>;
  formFieldDefinition: FFD;
};

export type IFieldState<FFD extends FormFieldDefinition> = (
  | IEditableFieldState<FFD>
  | INonEditableFieldState<FFD>
) &
  IFieldStateExternalDependency<FFD>;

export type CommandName =
  | 'SubmitRevision'
  | 'EditSubmission'
  | 'SubmitLicensing'
  | 'AcceptTransferOffer'
  | 'AuthorCompleteChangeRequest'
  | 'AuthorEditSubmission'
  | 'ChangeArticleType';

export const useSubmissionFields = (
  entryPoint:
    | 'review'
    | 'submission'
    | 'early-assessment'
    | 'adjudication' = 'submission'
) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { forms } = useWorkflow(entryPoint);
  const submissionFormFields = new SubmissionFormFields(
    forms.authorSubmission!,
    forms.authorRevision,
    forms.editorialLicensing
  );
  const commandContext = useCommandResponse();
  const setCommandResponse = commandContext?.setCommandResponse;
  const { fieldsResponse } =
    entryPoint === 'submission'
      ? useSubmissionData()
      : entryPoint === 'early-assessment'
      ? useAssessmentData()
      : entryPoint === 'adjudication'
      ? useAdjudicationData()
      : useReviewData();
  const fieldUpdater = FieldUpdater.fromGetFieldsResponse(fieldsResponse);
  const { submissionId, fields } = fieldsResponse;

  const { roleContext } =
    entryPoint === 'submission'
      ? getSubmissionRequestContextFromRoute()
      : entryPoint === 'early-assessment'
      ? getAssessmentRequestContextFromRoute()
      : entryPoint === 'adjudication'
      ? getAdjudicationRequestContextFromRoute()
      : getReviewRequestContextFromRoute();

  // TODO: Improvement for this function https://morressier.atlassian.net/browse/JEDIS-590
  const checkFormFieldValueIsUpdated = useCallback(
    (fieldName: string): boolean => {
      const field = fields[fieldName];

      if (!field) {
        return false;
      }

      const component = field.formFieldDefinition.component;
      const previousValue = field.previousValue as any;
      const value = field.value as any;

      switch (component) {
        case 'System:Title':
        case 'System:ArticleType':
        case 'System:CorrespondingAuthor': {
          return !!(previousValue && !_.isEqual(value, previousValue));
        }
        case 'Custom:RichTextInput':
          return !!(
            previousValue !== undefined &&
            value !== '' &&
            !_.isEqual(value, previousValue)
          );
        case 'System:NoveltyStatement':
        case 'Custom:Select':
        case 'Custom:FocusSelect':
        case 'System:AcceleratedPublication':
        case 'System:IPEMMembership':
        case 'System:TransparentPeerReview':
        case 'System:PreferenceImmediatePublication':
        case 'System:DoubleAnonymousPeerReview':
        case 'System:GeneratedDataset':
        case 'System:UtilizedDataset':
        case 'Custom:TextInput':
        case 'Custom:TextArea':
        case 'Custom:NumberInput':
        case 'Custom:IntegerInput':
        case 'Custom:DateInput':
        case 'Custom:RadioGroup':
        case 'System:ArticleSubtype': {
          return previousValue !== undefined && previousValue !== value;
        }

        case 'System:StandardReportingGuidelines': {
          const standardReportingGuidelinesDetails =
            fields['StandardReportingGuidelinesDetails'];
          const standardReportingGuidelinesDetailsPreviousValue =
            standardReportingGuidelinesDetails?.previousValue as string[];
          const standardReportingGuidelinesDetailsValue =
            standardReportingGuidelinesDetails?.value as string[];
          const ARRIVEFilesDetails = fields['ARRIVEFiles'];
          const CONSORTFilesDetails = fields['CONSORTFiles'];
          const StandardReportingGuidelinesFileDetails =
            fields['StandardReportingGuidelinesFileDetails'];

          const ARRIVEFilesPreviousValue =
            ARRIVEFilesDetails?.previousValue as any;
          const ARRIVEFilesValue = ARRIVEFilesDetails?.value as any;

          const CONSORTFilesPreviousValue =
            CONSORTFilesDetails?.previousValue as any;
          const CONSORTFilesValue = CONSORTFilesDetails?.value as any;

          const StandardReportingGuidelinesFileDetailsPreviousValue =
            StandardReportingGuidelinesFileDetails?.previousValue as any;
          const StandardReportingGuidelinesFileDetailsValue =
            StandardReportingGuidelinesFileDetails?.value as any;

          const standardReportingGuidelineTextConfig =
            fields['StandardReportingGuidelinesText'];

          const StandardReportingGuidelinesTextPreviousValue =
            standardReportingGuidelineTextConfig?.previousValue as any;
          const StandardReportingGuidelinesTextValue =
            standardReportingGuidelineTextConfig?.value as any;

          return (
            (previousValue !== undefined && previousValue !== value) ||
            (standardReportingGuidelinesDetailsPreviousValue &&
              standardReportingGuidelinesDetailsValue &&
              !_.isEqual(
                standardReportingGuidelinesDetailsValue,
                standardReportingGuidelinesDetailsPreviousValue
              )) ||
            (ARRIVEFilesPreviousValue !== undefined &&
              !_.isEqual(
                (ARRIVEFilesValue || []).map(getFileMetadata),
                (ARRIVEFilesPreviousValue || [])?.map(getFileMetadata)
              )) ||
            (CONSORTFilesPreviousValue !== undefined &&
              !_.isEqual(
                (CONSORTFilesValue || []).map(getFileMetadata),
                (CONSORTFilesPreviousValue || [])?.map(getFileMetadata)
              )) ||
            (StandardReportingGuidelinesFileDetailsPreviousValue !==
              undefined &&
              !_.isEqual(
                (StandardReportingGuidelinesFileDetailsValue || []).map(
                  getFileMetadata
                ),
                (
                  StandardReportingGuidelinesFileDetailsPreviousValue || []
                )?.map(getFileMetadata)
              )) ||
            (StandardReportingGuidelinesTextPreviousValue !== undefined &&
              !_.isEqual(
                StandardReportingGuidelinesTextValue,
                StandardReportingGuidelinesTextPreviousValue
              ))
          );
        }
        case 'System:Keywords':
        case 'Custom:TextInputArray':
        case 'System:Abstract':
        case 'System:Subtitle':
        case 'System:CommentForEditor':
          return !!(
            previousValue !== undefined && !_.isEqual(value, previousValue)
          );
        case 'Custom:StringInputArray':
          return !!(previousValue && !_.isEqual(value, previousValue));
        case 'System:EthicsStatement':
        case 'System:ClinicalTrials':
        case 'System:Funding':
        case 'System:FundingGeneric':
        case 'System:FiguresAndTables':
        case 'System:Preprint':
        case 'System:PreviouslySubmitted':
        case 'System:ConflictOfInterestForAuthor':
        case 'System:SharedDataset':
        case 'System:EthicalApproval':
        case 'System:EthicsApproval':
          return (
            previousValue !== undefined &&
            !_.isEqual(
              parseDependentField(value),
              parseDependentField(previousValue)
            )
          );
        case 'System:OpposedReviewers':
        case 'System:RecommendedReviewers': {
          const normalizedValue = {
            ...value,
            value: value?.value || []
          };
          const normalizedPreviousValue = {
            ...previousValue,
            value: previousValue?.value || []
          };
          return (
            previousValue !== undefined &&
            !_.isEqual(
              parseDependentField(normalizedValue),
              parseDependentField(normalizedPreviousValue)
            )
          );
        }
        case 'System:Supplementary':
        case 'System:AdditionalSupplementary':
        case 'System:SourceFiles':
        case 'Custom:FileUploadArray':
          return (
            typeof previousValue !== 'undefined' &&
            !_.isEqual(
              (value || []).map(getFileMetadata),
              (previousValue || [])?.map(getFileMetadata)
            )
          );
        case 'System:Manuscript':
        case 'System:TitlePage':
        case 'System:AnonymizedManuscript':
        case 'System:CoverLetter':
        case 'System:CompleteDocumentForReview':
        case 'System:ConsolidatedPdf':
        case 'System:AnonymizedConsolidatedPdf': {
          return (
            typeof previousValue !== 'undefined' &&
            !_.isEqual(getFileMetadata(value), getFileMetadata(previousValue!))
          );
        }
        case 'System:PublishingModel': {
          const modelValue = value?.model;
          const previousModelValue = previousValue?.model;
          return (
            previousModelValue !== undefined &&
            previousModelValue !== modelValue
          );
        }
        case 'System:DataAvailability': {
          const statusValue = value?.status;
          const previousStatusValue = previousValue?.status;
          return (
            previousStatusValue !== undefined &&
            previousStatusValue !== statusValue
          );
        }

        case 'System:Authors': {
          // Normalize contributions arrays to prevent false positives
          // This occurs due to mongoose initialising contribution as [],
          // but event sourcing initialising it as undefined.
          const normalizeAuthors = (authors?: any[]) =>
            (authors ?? []).map(author => ({
              firstName: author.firstName ?? null,
              middleName: author.middleName ?? null,
              lastName: author.lastName ?? null,
              email: author.email?.toLowerCase() ?? null,
              affiliation: author.affiliation ?? null,
              ringgoldId: author.ringgoldId ?? null,
              orcid: author.orcid ?? null,
              contributions: author.contributions || []
            }));

          const normalizedValue = normalizeAuthors(value);
          const normalizedPreviousValue = normalizeAuthors(previousValue);

          return (
            typeof previousValue !== 'undefined' &&
            !_.isEqual(normalizedValue, normalizedPreviousValue)
          );
        }
        case 'Custom:CheckboxArray': {
          const normalizedValue = omitId(value)?.map((item: any) => ({
            ...item,
            // Ensure each item has a 'value' property
            value: item.value || []
          }));
          const normalizedPreviousValue = omitId(previousValue)?.map(
            (item: any) => ({
              ...item,
              value: item.value || []
            })
          );
          return (
            typeof previousValue !== 'undefined' &&
            !_.isEqual(normalizedValue, normalizedPreviousValue)
          );
        }
        case 'System:Figures':
        case 'System:GraphicalAbstractImage':
        case 'System:Tables': {
          const isEmpty = (v: any) =>
            v === undefined ||
            v === null ||
            (Array.isArray(v) && v.length === 0);

          if (previousValue === undefined) {
            return false;
          }

          if (isEmpty(previousValue) && isEmpty(value)) {
            return false;
          }

          if (isEmpty(previousValue) || isEmpty(value)) {
            return true;
          }

          return !_.isEqual(
            getFiguresMetadata(previousValue),
            getFiguresMetadata(value)
          );
        }
        case 'System:AITools': {
          const normalizedValue = {
            ...value,
            exists: value?.exists ?? null,
            value: Array.isArray(value?.value) ? value.value : [],
            description: value?.description ?? ''
          };

          const normalizedPreviousValue = {
            ...previousValue,
            exists: previousValue?.exists ?? null,
            value: Array.isArray(previousValue?.value)
              ? previousValue.value
              : [],
            description: previousValue?.description ?? ''
          };

          return (
            previousValue !== undefined &&
            !_.isEqual(
              parseDependentField(normalizedValue),
              parseDependentField(normalizedPreviousValue)
            )
          );
        }

        default:
          return false;
      }
    },
    [fields]
  );

  const getOnChangeHandler = (
    fieldName: string
  ): ((value: any, showToast?: boolean) => void) | undefined => {
    const field = fields[fieldName];
    if (!field) {
      return undefined;
    }

    if (!field.editing.isEditable) {
      return undefined;
    }

    return async (value: unknown, showToast: boolean = true) => {
      setCommandResponse?.({ status: 'pending' });
      const response = await fieldUpdater.updateField(value, field);

      if (response?.isSuccess) {
        // Revalidate the request which should update the submission data.
        // TODO consider less wasteful way to update local state.
        queryClient.invalidateQueries(['submission-fields', submissionId]);
        queryClient.invalidateQueries([
          'change-requests',
          { submissionId, roleType: roleContext }
        ]);
        queryClient.invalidateQueries([
          'requested-change-fields',
          submissionId
        ]);
        setCommandResponse?.({ status: 'success' });
        showToast &&
          toast({
            variant: 'success',
            title: `${field.formFieldDefinition.params.labels.main} Updated`,
            isClosable: true
          });
      } else {
        if (response?.message?.error || response?.message?.warnings) {
          setCommandResponse?.({
            status: 'error',
            data: {
              error: response?.message?.error,
              warnings: response?.message?.warnings
            }
          });
        }
        fieldName !== 'SourceFiles' &&
          toast({
            variant: 'error',
            title: 'Error updating field',
            description: 'There was an error updating the field'
          });
      }
    };
  };

  const getExternalDependency = (path?: string) => {
    if (!path) return undefined;

    let value;
    for (const field of Object.values(fields)) {
      if (value) break;

      const objValue = _.set(
        {},
        field.formFieldDefinition.caseDataFields.main,
        field.value
      );
      value = _.get(objValue, path);
    }

    return {
      path,
      value
    };
  };

  return {
    getFieldState: <FFD extends FormFieldDefinition>(
      fieldName: FFD['name']
    ): IFieldState<FFD> | undefined => {
      const field = fields[fieldName];

      if (!field) {
        return undefined;
      }
      // @ts-ignore
      return {
        ...field,
        name: field.formFieldDefinition.name,
        isEditable: field.editing.isEditable
      };
    },
    submissionFormFields,
    getOnChangeHandler,
    checkFormFieldValueIsUpdated,
    getExternalDependency,
    fieldsResponse
  };
};

class FieldUpdater {
  public commandNameToMutationFn: Record<
    string,
    {
      mutateAsync: (
        ...args: any
      ) => Promise<{ isSuccess: true } | { isSuccess: false; message: any }>;
    }
  >;

  constructor(
    submissionId: string,
    submissionContextId: string,
    articleWorkflowId: string,
    organizationId: string
  ) {
    this.commandNameToMutationFn = {
      SubmitRevision: useSubmitRevision({
        submissionId,
        submissionContextId: submissionContextId,
        organizationId,
        articleWorkflowId: articleWorkflowId,
        subtype: 'fields',
        invalidateSubmissionQueries: undefined
      }),
      EditSubmission: useEditSubmissionMetadata({
        articleWorkflowId,
        submissionContextId,
        submissionId,
        organizationId
      }),
      SubmitLicensing: useSubmitLicensing({
        submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        articleWorkflowId: articleWorkflowId,
        subtype: 'fields',
        invalidateSubmissionQueries: undefined
      }),
      AcceptTransferOffer: useAcceptTransfer(submissionId, organizationId),
      AuthorCompleteChangeRequest: useAuthorCompleteChangeRequest({
        submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        articleWorkflowId: articleWorkflowId,
        subtype: 'fields',
        invalidateSubmissionQueries: undefined
      }),
      AuthorEditSubmission: useAuthorEditSubmission({
        submissionId,
        organizationId,
        submissionContextId: submissionContextId,
        articleWorkflowId: articleWorkflowId,
        subtype: 'fields',
        invalidateSubmissionQueries: undefined
      }),
      ChangeArticleType: useEditSubmissionArticleType({
        submissionContextId,
        submissionId,
        organizationId
      })
    };
  }

  static fromGetFieldsResponse(fieldsResponse: IGetSubmissionFieldsResponse) {
    const {
      submissionId,
      submissionContextId,
      articleWorkflowId,
      fields,
      organizationId
    } = fieldsResponse;

    const instance = new FieldUpdater(
      submissionId,
      submissionContextId,
      articleWorkflowId,
      organizationId
    );
    FieldUpdater.ensureRequiredCommandsAreSupported(instance, fields);

    return instance;
  }

  static ensureRequiredCommandsAreSupported(
    instance: FieldUpdater,
    fields: IGetSubmissionFieldsResponse['fields']
  ) {
    const requiredFieldUpdateCommands = Object.values(fields).reduce(
      (acc: Set<string>, curr) => {
        if (curr.editing.isEditable === true) {
          acc.add(curr.editing.via);
        }
        return acc;
      },
      new Set<string>()
    );

    const supportedFieldUpdateCommands = new Set(
      Object.keys(instance.commandNameToMutationFn)
    );

    const missingCommands = new Set(
      [...requiredFieldUpdateCommands].filter(
        cmd => !supportedFieldUpdateCommands.has(cmd)
      )
    );

    if (missingCommands.size > 0) {
      throw new Error(
        `Missing field-updating command support for: ${JSON.stringify(
          Array.from(requiredFieldUpdateCommands).join(', ')
        )}, got: ${JSON.stringify(
          Array.from(supportedFieldUpdateCommands).join(', ')
        )}`
      );
    }
  }

  updateField(
    value: unknown,
    field: ISubmissionFieldResponseObject<FormFieldDefinition>
  ) {
    if (!field.editing.isEditable) {
      return undefined;
    }

    const commandName = field.editing
      .via as keyof typeof this.commandNameToMutationFn;
    const commandMutation = this.commandNameToMutationFn[commandName];
    if (!commandMutation) {
      throw new Error(
        `Unsupported editing command ${
          field.editing.via
        }, available: ${JSON.stringify(
          Object.keys(this.commandNameToMutationFn).join(', ')
        )}`
      );
    }

    const fieldPath = field.formFieldDefinition.caseDataFields.main;
    const data: Record<string, unknown> = _.set({}, fieldPath, value);

    const isFileField = fieldPath.startsWith('fileSet.');
    let isFileUpdated: boolean | undefined;
    if (isFileField) {
      const filePath = fieldPath.slice('fileSet.'.length);
      const previousFileSet: Record<string, unknown> = {};
      const currentFileSet: Record<string, unknown> = {};
      _.set(previousFileSet, filePath, field.previousValue);
      _.set(currentFileSet, filePath, value);
      isFileUpdated =
        getSourceFileSetSignature(previousFileSet as ISubmissionFileSet) !==
        getSourceFileSetSignature(currentFileSet as ISubmissionFileSet);
    }

    if (
      commandName === 'SubmitRevision' ||
      commandName === 'AuthorEditSubmission'
    ) {
      const fieldName = fieldPath.split('.').pop()?.toLowerCase();
      if (
        fieldName === 'authorresponse' ||
        fieldName === 'authorresponseattachments'
      ) {
        data.isUpdated = !_.isEqual(value, field.value);
      }

      data.isFileUpdated = isFileUpdated ?? false;
    } else if (
      commandName === 'EditSubmission' &&
      isFileUpdated !== undefined
    ) {
      data.isFileUpdated = isFileUpdated;
    }

    return commandMutation.mutateAsync(data);
  }
}
